// =============================================================================
// SIREPPAR - Consultas a la SEP: detalle de una consulta (#388)
// =============================================================================
// Hilo cronológico con los mensajes del partido y de la SEP, y formulario de
// repregunta mientras la consulta no esté cerrada. El partido nunca ve las notas
// internas ni a quién está asignada: el backend no las manda por este camino.
// =============================================================================

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import RichTextEditor, { MarkdownDisplay } from '../../components/common/RichTextEditor';
import AdjuntosInforme, { MAX_ADJUNTOS, MAX_MB_ADJUNTO } from '../../components/common/AdjuntosInforme';
import EstadoConsultaBadge from '../../components/common/EstadoConsultaBadge';
import ReferenciaObservacionConsulta, { AvisoPlazoIntimacion, ObservacionReferencia } from '../../components/common/ReferenciaObservacionConsulta';
import { consultaService } from '../../services/api';
import { usePuedeCrearConsulta } from '../../hooks/usePuedeCrearConsulta';
import { CLAVE_PENDIENTES_PARTIDO } from '../../hooks/useConsultasPendientesPartido';
import {
  EstadoConsulta,
  OrigenMensaje,
  TEXTO_MAX,
  formatearFechaHora,
  formatearTamanio,
  mensajeDeError,
} from '../../utils/consultas';

interface Adjunto {
  id: string;
  nombreOriginal: string;
  mimeType: string;
  tamanioBytes: number;
}

interface Mensaje {
  id: string;
  texto: string;
  origen: OrigenMensaje;
  creadoEn: string;
  autor: { nombre: string; apellido: string } | null;
  adjuntos: Adjunto[];
}

interface ConsultaDetalle {
  id: string;
  asunto: string;
  estado: EstadoConsulta;
  expediente: { id: string; numero: string } | null;
  observacion: (ObservacionReferencia & { estado: string }) | null;
  /** #389: lo calcula el backend; acá solo se muestra. */
  intimacionVigente: boolean;
  mensajes: Mensaje[];
}

export default function ConsultaDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { puedeCrear } = usePuedeCrearConsulta();
  const [texto, setTexto] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['consulta', id],
    queryFn: () => consultaService.getById(id),
    enabled: !!id,
  });
  const consulta: ConsultaDetalle | undefined = data?.data;

  // Abrir la consulta la marca como vista en el backend: el contador del menú baja.
  useEffect(() => {
    if (!consulta?.id) return;
    queryClient.invalidateQueries({ queryKey: CLAVE_PENDIENTES_PARTIDO });
    queryClient.invalidateQueries({ queryKey: ['consultas'] });
  }, [consulta?.id, queryClient]);

  const repreguntar = useMutation({
    mutationFn: () => {
      const datos = new FormData();
      datos.append('texto', texto.trim());
      archivos.forEach((archivo) => datos.append('archivos', archivo));
      return consultaService.enviarMensaje(id, datos);
    },
    onSuccess: () => {
      toast.success('Mensaje enviado');
      setTexto('');
      setArchivos([]);
      queryClient.invalidateQueries({ queryKey: ['consulta', id] });
      queryClient.invalidateQueries({ queryKey: ['consultas'] });
      queryClient.invalidateQueries({ queryKey: CLAVE_PENDIENTES_PARTIDO });
    },
    onError: (e) => toast.error(mensajeDeError(e, 'No se pudo enviar el mensaje')),
  });

  const abrirAdjunto = async (adjuntoId: string) => {
    try {
      const res = await consultaService.getAdjuntoUrl(id, adjuntoId);
      window.open(res.data.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo abrir el adjunto'));
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Cargando...</p>;

  if (isError || !consulta) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-lg border border-gray-200 p-6 text-center">
        <p className="text-gray-700">No se encontró la consulta.</p>
        <Link to="/consultas" className="text-sm text-blue-600 hover:underline mt-2 inline-block">Volver a las consultas</Link>
      </div>
    );
  }

  const textoLimpio = texto.trim();
  const errorTexto = textoLimpio.length > TEXTO_MAX ? `El mensaje puede tener hasta ${TEXTO_MAX} caracteres.` : '';
  const puedeEnviar = textoLimpio.length > 0 && !errorTexto && !repreguntar.isPending;

  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/consultas" className="text-sm text-blue-600 hover:underline">← Volver a las consultas</Link>

      {/* Encabezado */}
      <div className="mt-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{consulta.asunto}</h1>
          <EstadoConsultaBadge
            estado={consulta.estado}
            recibida={consulta.mensajes.length > 0 && consulta.mensajes.every((m) => m.origen === 'SEP')}
          />
        </div>
        {consulta.mensajes[0]?.origen === 'SEP' && (
          <p className="text-sm text-gray-600 mt-1">Consulta iniciada por la Secretaría Electoral.</p>
        )}
        {consulta.expediente && (
          <p className="text-sm text-gray-600 mt-1">
            Expediente{' '}
            <Link to={`/expedientes/${consulta.expediente.id}`} className="text-blue-600 hover:underline">
              {consulta.expediente.numero}
            </Link>
          </p>
        )}
      </div>

      {/* Referencia a la observación o intimación (#389) */}
      {consulta.observacion && consulta.expediente && (
        <div className="mb-6 space-y-3">
          <ReferenciaObservacionConsulta observacion={consulta.observacion} expediente={consulta.expediente} conLink />
          {consulta.intimacionVigente && <AvisoPlazoIntimacion />}
        </div>
      )}

      {/* Hilo */}
      <div className="space-y-4">
        {consulta.mensajes.map((mensaje) => {
          const deLaSep = mensaje.origen === 'SEP';
          return (
            <div
              key={mensaje.id}
              className={`rounded-lg border p-4 ${deLaSep ? 'border-green-200 bg-green-50 ml-8' : 'border-gray-200 bg-white mr-8'}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-800">
                  {deLaSep ? 'Secretaría Electoral' : 'Su agrupación'}
                  {mensaje.autor && (
                    <span className="font-normal text-gray-500"> · {mensaje.autor.nombre} {mensaje.autor.apellido}</span>
                  )}
                </span>
                <span className="text-xs text-gray-500">{formatearFechaHora(mensaje.creadoEn)}</span>
              </div>
              <div className="text-sm text-gray-800">
                <MarkdownDisplay content={mensaje.texto} />
              </div>
              {mensaje.adjuntos.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {mensaje.adjuntos.map((adjunto) => (
                    <li key={adjunto.id}>
                      <button
                        type="button"
                        onClick={() => abrirAdjunto(adjunto.id)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {adjunto.nombreOriginal}
                      </button>
                      <span className="text-xs text-gray-500"> ({formatearTamanio(adjunto.tamanioBytes)})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Repregunta o aviso de cierre */}
      {consulta.estado === 'CERRADA' ? (
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          Esta consulta fue cerrada por la Secretaría.{' '}
          {puedeCrear && <Link to="/consultas/nueva" className="text-blue-600 hover:underline">Nueva consulta</Link>}
        </div>
      ) : (
        <form
          className="mt-6 bg-white rounded-lg border border-gray-200 p-4 space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (puedeEnviar) repreguntar.mutate(); }}
        >
          <span className="block text-sm font-medium text-gray-700">Agregar un mensaje</span>
          <RichTextEditor value={texto} onChange={setTexto} placeholder="Escriba su mensaje" minHeight={120} />
          {errorTexto && <p className="text-xs text-red-600">{errorTexto}</p>}
          <AdjuntosInforme
            archivos={archivos}
            onChange={setArchivos}
            ayuda={`Hasta ${MAX_ADJUNTOS} archivos PDF o imagen, de ${MAX_MB_ADJUNTO} MB cada uno.`}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!puedeEnviar}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
            >
              {repreguntar.isPending ? 'Enviando...' : 'Enviar mensaje'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
